import { PageError } from '@/components/PageError'
import config from '@/config'
import { getSessionToken } from '@/stores/session'
import { AUTHORIZATION_HEADER_PREFIX } from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'

/**
 * Debugging entry point: open a store's ExtraHub order tracking from Fuze.
 *
 * Not linked anywhere in the UI. Navigate to /track/<erp> directly.
 *
 * Why this exists rather than just linking at the backend route: the handoff
 * needs the caller's session, and a browser navigating to the backend cannot
 * send an Authorization header. The only header-free alternative is putting the
 * session token in the URL, which is the pattern the exchange-code work removed.
 * So this route does the authenticated call itself and then navigates the
 * browser to the URL it gets back. The only token that ever reaches a URL is the
 * 120-second, single-ERP handoff token.
 *
 * It asks for JSON because a cross-origin fetch cannot read the 302's Location
 * header, and following the redirect would hit Netlify, which sends no CORS
 * headers for this origin.
 */
export const Route = createFileRoute('/_auth/track/$erp')({
  loader: async ({ params }) => {
    const sessionToken = getSessionToken()
    if (!sessionToken) throw new Error('No active session')

    const res = await fetch(
      `${config.backendURL}/saml/extra/internal?erp=${encodeURIComponent(params.erp)}`,
      {
        headers: {
          Accept: 'application/json',
          Authorization: `${AUTHORIZATION_HEADER_PREFIX}${sessionToken}`,
        },
      },
    )

    if (!res.ok) {
      throw new Error(`Could not open order tracking for ${params.erp} (${res.status})`)
    }

    const { url }: { url: string } = await res.json()

    // replace() so the back button returns to where the user came from rather
    // than bouncing through this route again and re-minting a token.
    window.location.replace(url)

    return { url }
  },
  // Nothing to render: the loader replaces the location before paint. Returning
  // null avoids adding i18n keys for a debug-only route.
  component: () => null,
  errorComponent: ({ error }) => <PageError message={error.message} global />,
})
